import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  Cell,
  CartesianGrid
} from 'recharts';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import ProgressChart from './ProgressChart';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Multiselect from 'multiselect-react-dropdown';
import {
  getEngineerStatsByProjectId,
  getEngTeamStatsByProjectId,
  getCommonProjectProgress,
  getIndependentProjectProgress,
  getGroupProjectProgress,
  getMoneyUtilizationStats,
  getAllSectionsByProject,
  getUnitsByProjectId,
  getAllBlocks
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';

const EngGraphs = ({ projectID, month }) => {
  const { register: formRegister, watch, setValue, control } = useForm();
  const userID = Number(Cookies.get('userId'));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State management
  const [showTeamIndividual, setShowTeamIndividual] = useState(false);
  const [lastCommittedField, setLastCommittedField] = useState(null);
  const [progressResponse, setProgressResponse] = useState(null);
  const [independentProgressResponse, setIndependentProgressResponse] = useState(null);
  const [groupProgressResponse, setGroupProgressResponse] = useState(null);
  const [moneyUtilizationResponse, setMoneyUtilizationResponse] = useState(null);
  const [moneyUtilizationResponse2, setMoneyUtilizationResponse2] = useState(null);
  const [moneyUtilizationResponse3, setMoneyUtilizationResponse3] = useState(null);

  // Form watched values
  const selectedSections = watch('section_ids');
  const selectedUnits = watch('unit_ids');
  const selectedBlocks = watch('block_ids');
  const selectedSections2 = watch('section_ids2');
  const selectedUnits2 = watch('unit_ids2');
  const selectedBlocks2 = watch('block_ids2');

  // API Queries
  const { data: AllUnits, refetch: fetchUnits } = useQuery({
    queryKey: ['AllUnits', projectID],
    queryFn: () => getUnitsByProjectId(projectID),
    enabled: !!projectID
  });

  const { data: AllBlocks, refetch: refetchBlocks } = useQuery({
    queryKey: ['getAllBlocks', projectID],
    queryFn: () => getAllBlocks(projectID),
    enabled: !!projectID
  });

  const { data: AllSections = [] } = useQuery({
    queryKey: ['sections', projectID],
    queryFn: async () => {
      if (!projectID) return [];
      try {
        const sections = await getAllSectionsByProject(projectID);
        return sections || [];
      } catch (error) {
        console.error('Error fetching sections:', error);
        return [];
      }
    },
    enabled: !!projectID
  });

  const {
    data: userStats,
    refetch: refetStats,
    isLoading: isLoadingStats
  } = useQuery({
    queryKey: ['userStats', projectID, month],
    queryFn: () => getEngineerStatsByProjectId(projectID, month),
    enabled: !!projectID && !!month,
    onSuccess: (data) => {
      mixpanel.track('Engineer Stats API Success', {
        user_id: userID,
        project_id: projectID,
        month: month,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Engineer Stats API Failed', {
        user_id: userID,
        project_id: projectID,
        month: month,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: engTeamStats,
    refetch: refetchEngTeamStats,
    isLoading: isLoadingEngTeamStats
  } = useQuery({
    queryKey: ['engTeamStats', projectID, month],
    queryFn: () => getEngTeamStatsByProjectId(projectID, month),
    enabled: !!projectID && !!month,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      mixpanel.track('Eng Team Stats API Success', {
        user_id: userID,
        project_id: projectID,
        month: month,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Eng Team Stats API Failed', {
        user_id: userID,
        project_id: projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // API Mutations
  const { mutate: handleCommonProjectProgress } = useMutation({
    mutationFn: (payload) => getCommonProjectProgress(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);
      console.log('commonProjectsProgress', result);
      setProgressResponse(result);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error fetching common project progress', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error fetching common project progress'
      );
    }
  });

  const { mutate: handleIndependentProjectProgress } = useMutation({
    mutationFn: (payload) => getIndependentProjectProgress(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);
      console.log('independentProjectsProgress', result);
      setIndependentProgressResponse(result);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error fetching independent project progress', err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Error fetching independent project progress'
      );
    }
  });

  const { mutate: handleGroupProjectProgress } = useMutation({
    mutationFn: (payload) => getGroupProjectProgress(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);
      console.log('groupProjectsProgress', result);
      setGroupProgressResponse(result);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error fetching group project progress', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error fetching group project progress'
      );
    }
  });

  const { mutate: handleMoneyUtilizationStats } = useMutation({
    mutationFn: (payload) => getMoneyUtilizationStats(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);
      console.log('moneyUtilizationStats', result);
      setMoneyUtilizationResponse(result);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error fetching money utilization stats', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error fetching money utilization stats'
      );
    }
  });

  const { mutate: handleMoneyUtilizationStats2 } = useMutation({
    mutationFn: (payload) => getMoneyUtilizationStats(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);
      console.log('moneyUtilizationStats2', result);
      setMoneyUtilizationResponse2(result);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error fetching money utilization stats2', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error fetching money utilization stats2'
      );
    }
  });

  const { mutate: handleMoneyUtilizationStats3 } = useMutation({
    mutationFn: (payload) => getMoneyUtilizationStats(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);
      console.log('moneyUtilizationStats3', result);
      setMoneyUtilizationResponse3(result);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error fetching money utilization stats', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error fetching money utilization stats'
      );
    }
  });

  // API call configuration
  const apiCallConfig = [
    {
      fieldName: 'section_ids',
      selectedData: selectedSections,
      apiFunction: handleCommonProjectProgress,
      payloadBuilder: (data) => {
        const sectionsByWorkType = {};
        data.forEach((section) => {
          if (!sectionsByWorkType[section.workType_id]) {
            sectionsByWorkType[section.workType_id] = [];
          }
          sectionsByWorkType[section.workType_id].push(section.id);
        });
        return {
          project_id: projectID,
          inputs: Object.entries(sectionsByWorkType).map(([workType_id, sections]) => ({
            workType_id: parseInt(workType_id),
            sections: sections
          }))
        };
      }
    },
    {
      fieldName: 'section_ids2',
      selectedData: selectedSections2,
      apiFunction: handleMoneyUtilizationStats,
      payloadBuilder: (data) => {
        const sectionsByWorkType = {};
        data.forEach((section) => {
          if (!sectionsByWorkType[section.workType_id]) {
            sectionsByWorkType[section.workType_id] = [];
          }
          sectionsByWorkType[section.workType_id].push(section.id);
        });
        return {
          project_id: projectID,
          taskType: 'Common',
          inputs: Object.entries(sectionsByWorkType).map(([workType_id, sections]) => ({
            workType_id: parseInt(workType_id),
            sections: sections
          }))
        };
      }
    },
    {
      fieldName: 'unit_ids',
      selectedData: selectedUnits,
      apiFunction: handleIndependentProjectProgress,
      payloadBuilder: (data) => ({
        project_id: projectID,
        units: data.map((unit) => unit.id)
      })
    },
    {
      fieldName: 'unit_ids2',
      selectedData: selectedUnits2,
      apiFunction: handleMoneyUtilizationStats2,
      payloadBuilder: (data) => ({
        project_id: projectID,
        taskType: 'Independent',
        units: data.map((unit) => unit.id)
      })
    },
    {
      fieldName: 'block_ids',
      selectedData: selectedBlocks,
      apiFunction: handleGroupProjectProgress,
      payloadBuilder: (data) => ({
        project_id: projectID,
        blocks: data.map((block) => block.id)
      })
    },
    {
      fieldName: 'block_ids2',
      selectedData: selectedBlocks2,
      apiFunction: handleMoneyUtilizationStats3,
      payloadBuilder: (data) => ({
        project_id: projectID,
        taskType: 'Group',
        blocks: data.map((block) => block.id)
      })
    }
  ];

  // Constants and configurations
  const autoSelectConfig = [
    {
      dataSource: AllSections,
      fieldName: 'section_ids',
      condition: () => !selectedSections?.length,
      transform: (item) => ({
        name: item.section_name,
        id: item.section_id,
        workType_id: item.workType_id
      })
    },
    {
      dataSource: AllSections,
      fieldName: 'section_ids2',
      condition: () => !selectedSections2?.length,
      transform: (item) => ({
        name: item.section_name,
        id: item.section_id,
        workType_id: item.workType_id
      })
    },
    {
      dataSource: AllUnits,
      fieldName: 'unit_ids',
      condition: () => !selectedUnits?.length,
      transform: (item) => ({
        name: item.unitName,
        id: item.id
      }),
      filter: (items) =>
        items.filter(
          (item) =>
            item.accommodation?.accomodationType === 'INDEPENDENT' &&
            item.accommodation?.numberOfFloors !== null
        )
    },
    {
      dataSource: AllUnits,
      fieldName: 'unit_ids2',
      condition: () => !selectedUnits2?.length,
      transform: (item) => ({
        name: item.unitName,
        id: item.id
      }),
      filter: (items) =>
        items.filter(
          (item) =>
            item.accommodation?.accomodationType === 'INDEPENDENT' &&
            item.accommodation?.numberOfFloors !== null
        )
    },
    {
      dataSource: AllBlocks,
      fieldName: 'block_ids',
      condition: () => !selectedBlocks?.length,
      transform: (item) => ({
        name: item.name,
        id: item.id
      }),
      filter: (items) => items?.filter((item) => item?.type === 'Group')
    },
    {
      dataSource: AllBlocks,
      fieldName: 'block_ids2',
      condition: () => !selectedBlocks2?.length,
      transform: (item) => ({
        name: item.name,
        id: item.id
      }),
      filter: (items) => items?.filter((item) => item?.type === 'Group')
    }
  ];

  // Data transformation functions
  const transformedDataSections = AllSections?.map((item) => ({
    name: item.section_name?.trim(),
    id: item.section_id,
    workType_id: item.workType_id
  }));

  const transformedDataUnits =
    AllUnits?.filter(
      (item) =>
        item.accommodation?.accomodationType === 'INDEPENDENT' &&
        item.accommodation?.numberOfFloors !== null
    ).map((item) => ({
      name: item.unitName || '',
      id: item.id,
      value: item.id
    })) || [];

  const transformedDataBlocks = (AllBlocks || [])
    .filter((item) => item?.type === 'Group')
    ?.map((item) => ({
      name: item.name || '',
      id: item.id,
      value: item.id
    }));

  // Chart data processing
  const chartData = (engTeamStats?.data || [])
    .filter(
      (item) =>
        item.vertical === 'Operations' && item.level === (showTeamIndividual ? 'Low' : 'Medium')
    )
    .map((item) => ({
      name: item.name,
      performance: item.performance || 0,
      level: item.level,
      color: item.level === 'Medium' ? '#0EA5E9' : '#10B981'
    }));

  const maxNameLength = chartData.reduce((max, item) => Math.max(max, (item.name || '').length), 0);
  const xAxisHeight = Math.max(60, Math.min(200, 30 + maxNameLength * 5));

  // Chart component functions
  const CustomYAxisLabel = ({ viewBox }) => {
    const { y, height, x } = viewBox;
    return (
      <text
        x={x - 0}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        transform={`rotate(-90, ${x - 0}, ${y + height / 2})`}
        style={{ fontSize: 16, fill: '#666' }}>
        Performance (%)
      </text>
    );
  };

  const TopRoundedBar1 = (props) => {
    const { x, y, width, height, fill, payload } = props;
    if (payload.value === 0) return null;
    const radius = 6;
    return (
      <path
        d={`
          M${x},${y + height}
          V${y + radius}
          Q${x},${y} ${x + radius},${y}
          H${x + width - radius}
          Q${x + width},${y} ${x + width},${y + radius}
          V${y + height}
          Z
        `}
        style={{ cursor: 'pointer' }}
        fill={fill}
      />
    );
  };

  const CircularProgress = ({ percentage, title, color, size = 120 }) => {
    const radius = (size - 20) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative" style={{ width: size, height: size }}>
          <svg className="transform -rotate-90" width={size} height={size}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="10"
              fill="transparent"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold text-gray-700">
              {percentage > 0 ? `${percentage}%` : '---'}
            </span>
          </div>
        </div>
        <span className="text-sm font-medium text-gray-600 mt-2">{title}</span>
      </div>
    );
  };

  // Event handlers
  const handleTeamLevelToggle = () => {
    const newValue = !showTeamIndividual;
    setShowTeamIndividual(newValue);
    mixpanel.track('Team Level View Toggle', {
      user_id: userID,
      view_type: newValue ? 'Low Level' : 'Mid Level',
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  const handleSelectionChange = (fieldName, selectedList) => {
    setValue(fieldName, selectedList);
    setLastCommittedField(fieldName);
  };

  // Single useEffect for API calls - only trigger for the last committed field
  useEffect(() => {
    if (!lastCommittedField) return;
    apiCallConfig.forEach((config) => {
      if (config.fieldName === lastCommittedField && config.selectedData?.length > 0 && projectID) {
        const payload = config.payloadBuilder(config.selectedData);
        console.log('Calling API with payload:', payload);
        config.apiFunction(payload);
      }
    });
  }, [
    lastCommittedField,
    selectedSections,
    selectedSections2,
    selectedUnits,
    selectedUnits2,
    selectedBlocks,
    selectedBlocks2,
    projectID
  ]);

  // Auto-select first items when data is loaded
  useEffect(() => {
    autoSelectConfig.forEach((config) => {
      if (config.dataSource?.length > 0 && config.condition()) {
        const items = config.filter ? config.filter(config.dataSource) : config.dataSource;
        if (items.length > 0) {
          const firstItem = items[0];
          const data = [config.transform(firstItem)];
          setValue(config.fieldName, data);
        }
      }
    });
  }, [AllSections, AllUnits, AllBlocks, setValue]);

  // Clear selections and responses when project changes
  useEffect(() => {
    setLastCommittedField(null);
    setValue('section_ids', []);
    setValue('section_ids2', []);
    setValue('unit_ids', []);
    setValue('unit_ids2', []);
    setValue('block_ids', []);
    setValue('block_ids2', []);
    setProgressResponse(null);
    setIndependentProgressResponse(null);
    setGroupProgressResponse(null);
    setMoneyUtilizationResponse(null);
    setMoneyUtilizationResponse2(null);
    setMoneyUtilizationResponse3(null);
  }, [projectID, setValue]);

  // Refetch data when project or month changes
  useEffect(() => {
    if (projectID) {
      fetchUnits();
      refetchBlocks();
    }
  }, [projectID, fetchUnits, refetchBlocks]);

  useEffect(() => {
    if (projectID && month) {
      refetStats();
      refetchEngTeamStats();
    }
  }, [projectID, month, refetStats, refetchEngTeamStats]);

  return (
    <div className="flex flex-col gap-5 transition-all duration-300 ease-in-out w-full justify-center items-center">
      {/* Engineer Stats */}
      <div className="p-6 w-full max-w-4xl rounded-xl shadow bg-white">
        <h3 className="text-lg font-semibold mb-6 text-gray-800">Performance Overview</h3>

        <div className="flex justify-around items-center">
          <CircularProgress
            percentage={userStats?.data?.dailyTaskPercentage || 0}
            title="Tasks"
            color="#0EA5E9"
            size={140}
          />
          <CircularProgress
            percentage={Math.min(userStats?.data?.qualityScorePercentage || 0, 100)}
            title="Quality"
            color="#10B981"
            size={140}
          />
          <CircularProgress
            percentage={userStats?.data?.performance || 0}
            title="Performance"
            color="#F59E0B"
            size={140}
          />
        </div>
      </div>

      {/* Operations Team Performance */}
      <div className="p-4 pb-0 w-full max-w-4xl mx-auto bg-white rounded-xl flex flex-col justify-start items-center shadow gap-3">
        <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
          Operations Team Performance
        </h3>

        <div className="bg-[#d5d6d6] h-[2px] w-[80%] mb-4" />

        {/* Toggle Control */}
        <div className="flex justify-between w-full max-w-4xl px-4 mb-4">
          <div className="flex gap-4">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-[#0EA5E9] rounded-sm"></div>
              <span className="text-xs">Mid Level</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
              <span className="text-xs">Low Level</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span
              className={`font-semibold ${!showTeamIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
              Mid Level
            </span>
            <div
              className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 bg-gray-300`}
              onClick={handleTeamLevelToggle}>
              <div
                className={`w-4 h-4 bg-[#346C75] rounded-full absolute top-0.5 transition-all duration-300 ${
                  showTeamIndividual ? 'right-0.5' : 'left-0.5'
                }`}></div>
            </div>
            <span
              className={`font-semibold ${showTeamIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
              Low Level
            </span>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          {engTeamStats?.data &&
          engTeamStats.data.filter(
            (item) =>
              item.vertical === 'Operations' &&
              item.level === (showTeamIndividual ? 'Low' : 'Medium')
          ).length > 0 ? (
            <BarChart data={chartData} margin={{ top: 20, right: 20, left: 30, bottom: 1 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={xAxisHeight}
                interval={0}
              />
              <YAxis
                type="number"
                domain={[0, 100]}
                allowDataOverflow={false}
                allowDecimals={false}
                label={<CustomYAxisLabel />}
              />
              <Bar
                dataKey="performance"
                shape={<TopRoundedBar1 />}
                style={{ cursor: 'pointer' }}
                isAnimationActive={false}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.performance === 0 ? 'transparent' : entry.color}
                  />
                ))}
                <LabelList
                  dataKey="performance"
                  content={({ x, y, width, height, value }) => {
                    if (value === 0) return null;
                    return (
                      <text
                        x={x + width / 2}
                        y={y + 15}
                        textAnchor="middle"
                        style={{
                          fontSize: 12,
                          fill: '#fff',
                          fontWeight: 500
                        }}>
                        {value}%
                      </text>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
              No {showTeamIndividual ? 'Low Level' : 'Mid Level'} Operations team members available
            </div>
          )}
        </ResponsiveContainer>
      </div>

      {/* Progress Charts */}
      <ProgressChart
        title="Projects Progress (Common Tasks)"
        chartType="progress"
        rawData={progressResponse}
        isSubmitting={isSubmitting}
        control={control}
        options={[
          {
            label: 'Sections',
            name: 'section_ids',
            data: transformedDataSections
          }
        ]}
        selectedValues={{
          section_ids: selectedSections
        }}
        onSelectionChange={handleSelectionChange}
        xAxisLabel="Days"
      />

      <ProgressChart
        title="Project Progress (Independent Tasks)"
        chartType="progress"
        rawData={independentProgressResponse}
        isSubmitting={isSubmitting}
        control={control}
        options={[
          {
            label: 'Units',
            name: 'unit_ids',
            data: transformedDataUnits
          }
        ]}
        selectedValues={{
          unit_ids: selectedUnits
        }}
        onSelectionChange={handleSelectionChange}
        xAxisLabel="Days"
      />

      <ProgressChart
        title="Project Progress (Group Tasks)"
        chartType="progress"
        rawData={groupProgressResponse}
        isSubmitting={isSubmitting}
        control={control}
        options={[
          {
            label: 'Blocks',
            name: 'block_ids',
            data: transformedDataBlocks
          }
        ]}
        selectedValues={{
          block_ids: selectedBlocks
        }}
        onSelectionChange={handleSelectionChange}
        xAxisLabel="Days"
      />

      {/* Money Utilization Charts */}
      <ProgressChart
        title="Money Utilization (Common Tasks)"
        chartType="money"
        rawData={moneyUtilizationResponse}
        isSubmitting={isSubmitting}
        control={control}
        options={[
          {
            label: 'Sections',
            name: 'section_ids2',
            data: transformedDataSections
          }
        ]}
        selectedValues={{
          section_ids2: selectedSections2
        }}
        onSelectionChange={handleSelectionChange}
        xAxisLabel="Amount"
      />

      <ProgressChart
        title="Money Utilization (Independent Tasks)"
        chartType="money"
        rawData={moneyUtilizationResponse2}
        isSubmitting={isSubmitting}
        control={control}
        options={[
          {
            label: 'Units',
            name: 'unit_ids2',
            data: transformedDataUnits
          }
        ]}
        selectedValues={{
          unit_ids2: selectedUnits2
        }}
        onSelectionChange={handleSelectionChange}
        xAxisLabel="Amount"
      />

      <ProgressChart
        title="Money Utilization (Group Tasks)"
        chartType="money"
        rawData={moneyUtilizationResponse3}
        isSubmitting={isSubmitting}
        control={control}
        options={[
          {
            label: 'Blocks',
            name: 'block_ids2',
            data: transformedDataBlocks
          }
        ]}
        selectedValues={{
          block_ids2: selectedBlocks2
        }}
        onSelectionChange={handleSelectionChange}
        xAxisLabel="Amount"
      />
    </div>
  );
};

export default EngGraphs;
